import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";

const Brand = ({ className = "", w = "w-[180px]" }) => (
  <img src="/brand/kewagpage-logo-white.png" alt="KEWAGPage" data-testid="brand-logo" draggable="false"
    className={`${w} h-auto object-contain block select-none ${className}`} />
);

// Spiegelt die serverseitigen Passwortregeln (min. 10, Gross/Klein/Ziffer/Sonderzeichen).
const rules = (pw) => ([
  { ok: (pw || "").length >= 10, label: "Mindestens 10 Zeichen" },
  { ok: /[a-z]/.test(pw || ""), label: "Ein Kleinbuchstabe" },
  { ok: /[A-Z]/.test(pw || ""), label: "Ein Grossbuchstabe" },
  { ok: /\d/.test(pw || ""), label: "Eine Ziffer" },
  { ok: /[^A-Za-z0-9]/.test(pw || ""), label: "Ein Sonderzeichen" },
]);

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const checks = useMemo(() => rules(pw), [pw]);
  const allOk = checks.every((c) => c.ok);
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) { setError(t("Bitte erfüllen Sie alle Passwortanforderungen.")); return; }
    if (!match) { setError(t("Die Passwörter stimmen nicht überein.")); return; }
    setBusy(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      toast.success(t("Passwort geändert. Bitte melden Sie sich neu an."));
    } catch (err) {
      setError(err.response?.data?.detail || t("Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md fade-in">
        <div className="flex items-center justify-between mb-8 gap-2">
          <Brand w="w-[150px]" />
          <div className="flex gap-1 ml-auto">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${lang === l.code ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-sm p-6 sm:p-8 shadow-2xl shadow-black/40">
          <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mb-4">
            <KeyRound className="w-5 h-5 text-[#A3E635]" />
          </div>

          {!token ? (
            <div data-testid="reset-no-token">
              <h1 className="text-2xl font-black tracking-tighter">{t("Ungültiger Link")}</h1>
              <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 mt-4 mb-5">
                <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-200">{t("Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.")}</p>
              </div>
              <Link to="/login" data-testid="reset-to-login"
                className="inline-block bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-6 rounded-lg transition-colors">
                {t("Zur Anmeldung")}
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-6 h-6 text-[#A3E635]" />
                <h1 className="text-2xl font-black tracking-tighter">{t("Passwort geändert")}</h1>
              </div>
              <p className="text-gray-400 mt-1 mb-5 text-sm">
                {t("Ihr Passwort wurde erfolgreich zurückgesetzt. Bitte melden Sie sich mit Ihrem neuen Passwort an.")}
              </p>
              <button onClick={() => nav("/login", { replace: true })} data-testid="reset-goto-login"
                className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors">
                {t("Jetzt anmelden")}
              </button>
            </div>
          ) : (
            <div data-testid="reset-form-panel">
              <h1 className="text-2xl font-black tracking-tighter">{t("Neues Passwort festlegen")}</h1>
              <p className="text-gray-400 mt-1 mb-6 text-sm">{t("Wählen Sie ein sicheres neues Passwort für Ihr Konto.")}</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("Neues Passwort")}</label>
                  <input data-testid="reset-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="••••••••" autoFocus />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t("Passwort bestätigen")}</label>
                  <input data-testid="reset-password-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="mt-1.5 w-full bg-black border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors"
                    placeholder="••••••••" />
                </div>

                <ul data-testid="reset-rules" className="grid grid-cols-1 gap-1 text-xs">
                  {checks.map((c) => (
                    <li key={c.label} className={`flex items-center gap-2 ${c.ok ? "text-[#A3E635]" : "text-gray-500"}`}>
                      <CheckCircle2 className={`w-3.5 h-3.5 ${c.ok ? "opacity-100" : "opacity-30"}`} /> {t(c.label)}
                    </li>
                  ))}
                  <li className={`flex items-center gap-2 ${match ? "text-[#A3E635]" : "text-gray-500"}`}>
                    <CheckCircle2 className={`w-3.5 h-3.5 ${match ? "opacity-100" : "opacity-30"}`} /> {t("Passwörter stimmen überein")}
                  </li>
                </ul>

                {error && (
                  <div data-testid="reset-error" className="flex items-start gap-2 rounded-xl border border-red-400/30 bg-red-400/10 p-3">
                    <AlertTriangle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <button data-testid="reset-submit" type="submit" disabled={busy || !allOk || !match}
                  className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                  {busy ? "..." : t("Passwort zurücksetzen")}
                </button>
              </form>

              <Link to="/login" data-testid="reset-cancel" className="mt-4 block text-xs text-gray-400 hover:text-white transition-colors">
                {t("← Zurück zur Anmeldung")}
              </Link>
            </div>
          )}
        </div>

        <div data-testid="trust-badge" className="mt-6 flex items-center justify-center gap-4 text-[11px] text-gray-500">
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#A3E635]" /> {t("Verschlüsselte Verbindung")}</span>
          <span className="text-gray-700">·</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#A3E635]" /> {t("Sicherer Einmal-Link")}</span>
        </div>
      </div>
    </div>
  );
}
